import { ref, watch, type Ref } from 'vue'

import { useCore } from '@/composables/core'
import { useOnboardingStore } from '@/stores/onboarding'
import { getUserPrefixedPhone, getBusinessPrefixedPhone } from '@/lib/analytics/helpers'
import { hashedEmail, hashedPhone } from '@/lib/analytics/advanced-matching/hash'

type AdvancedMatchingUser = {
  email?: string
  cell_phone?: string
  cell_phone_with_prefix?: string
}

type AdvancedMatchingBusiness = {
  owner_email?: string
  phone?: string
  phone_with_prefix?: string
}

type AdvancedMatchingRootState = {
  app: { country: string }
  identity?: { user?: AdvancedMatchingUser | null }
  business?: { business?: AdvancedMatchingBusiness | null }
}

type AdvancedMatchingSources = {
  email?: string
  phone?: string
}

export type AdvancedMatchingTraits = {
  email?: string
  phone?: string
}

const hashedEmailRef: Ref<string | null> = ref(null)
const hashedPhoneRef: Ref<string | null> = ref(null)

let stopWatchingSources: (() => void) | null = null

// Guards against out-of-order resolution: hashing is async and its duration depends on
// the input (empty sources resolve immediately, see hash.ts), so a run started earlier
// can settle after a more recent one. Only the run matching the latest id may commit.
let latestRunId = 0

// Node's `crypto.subtle.digest` settles via the thread pool (not microtasks), so tests
// cannot rely on `flushPromises` alone — await this instead.
let latestRecompute: Promise<void> = Promise.resolve()

function resolveSources (): AdvancedMatchingSources {
  const { store } = useCore()
  const onboardingStore = useOnboardingStore()
  const state = store.state as AdvancedMatchingRootState

  const user = state.identity?.user || (onboardingStore.user as AdvancedMatchingUser)
  const business = state.business?.business || (onboardingStore.business as AdvancedMatchingBusiness)
  const country = state.app.country

  return {
    email: user?.email || business?.owner_email,
    phone: getUserPrefixedPhone({ user, country }) || getBusinessPrefixedPhone({ business, country }),
  }
}

async function recomputeAdvancedMatching ({ email, phone }: AdvancedMatchingSources, runId: number): Promise<void> {
  const [nextHashedEmail, nextHashedPhone] = await Promise.all([
    hashedEmail(email),
    hashedPhone(phone),
  ])

  if (runId !== latestRunId) {
    return
  }

  hashedEmailRef.value = nextHashedEmail
  hashedPhoneRef.value = nextHashedPhone
}

/**
 * Registers the watcher that keeps the hashed Advanced Matching traits in
 * sync with the current user/business (login, `reloadUser`, business switch,
 * onboarding user set). Safe to call more than once — re-registers cleanly
 * against whatever `identity`/`business`/onboarding state is current, which
 * is also what keeps it test-friendly. Call once during analytics bootstrap
 * so hashing starts on first load (`immediate: true`), not only on the next
 * change.
 */
export function initAdvancedMatching (): void {
  stopWatchingSources?.()

  // Watch a stable primitive (not a fresh object each time) so Vue compares by
  // value and does not re-fire solely due to object identity.
  stopWatchingSources = watch(
    () => {
      const { email, phone } = resolveSources()
      return `${email ?? ''}\0${phone ?? ''}`
    },
    (serialized) => {
      const separator = serialized.indexOf('\0')
      const email = serialized.slice(0, separator) || undefined
      const phone = serialized.slice(separator + 1) || undefined
      const runId = ++latestRunId

      latestRecompute = recomputeAdvancedMatching({ email, phone }, runId)
      void latestRecompute
    },
    { immediate: true, flush: 'sync' }
  )
}

/**
 * Awaits the in-flight hash recompute. Needed in tests because Node's
 * `crypto.subtle.digest` does not settle as a microtask (`flushPromises` is insufficient).
 */
export function flushAdvancedMatching (): Promise<void> {
  return latestRecompute
}

/**
 * Tears down watcher + module state between tests.
 */
export function resetAdvancedMatching (): void {
  stopWatchingSources?.()
  stopWatchingSources = null
  hashedEmailRef.value = null
  hashedPhoneRef.value = null
  latestRunId = 0
  latestRecompute = Promise.resolve()
}

/**
 * Synchronous, best-effort getter for the analytics dispatch path (which is
 * itself synchronous). Returns only the hashed traits whose digest has
 * already resolved — a still-pending hash is simply omitted, never awaited.
 */
export function getAdvancedMatchingTraits (): AdvancedMatchingTraits {
  if (!stopWatchingSources) {
    initAdvancedMatching()
  }

  const traits: AdvancedMatchingTraits = {}

  if (hashedEmailRef.value) {
    traits.email = hashedEmailRef.value
  }

  if (hashedPhoneRef.value) {
    traits.phone = hashedPhoneRef.value
  }

  return traits
}
